import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Megaphone, Gift, Save, Plus, Trash2, ToggleLeft, ToggleRight, Eye } from "lucide-react";
import MarqueeBar from "@/components/MarqueeBar";

const inputDark = "w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-cream placeholder:text-cream/30 focus:border-[#C9789A] focus:outline-none transition";

const AdminMarketing = () => {
  const [data, setData] = useState(null);
  const [savingMarquee, setSavingMarquee] = useState(false);
  const [savingPopup, setSavingPopup] = useState(false);

  const load = () =>
    api.get("/admin/marketing").then((r) => setData(r.data)).catch(() => toast.error("Could not load"));

  useEffect(() => { load(); }, []);

  const setMarquee = (k, v) => setData({ ...data, marquee: { ...data.marquee, [k]: v } });
  const setPopup = (k, v) => setData({ ...data, popup: { ...data.popup, [k]: v } });

  const saveMarquee = async () => {
    setSavingMarquee(true);
    try {
      await api.patch("/admin/marketing", { marquee: data.marquee });
      toast.success("Announcement bar saved — visible on the storefront immediately.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingMarquee(false); }
  };

  const savePopup = async () => {
    setSavingPopup(true);
    try {
      await api.patch("/admin/marketing", { popup: data.popup });
      toast.success("Discount popup saved.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingPopup(false); }
  };

  const previewPopup = () => {
    // Clear cooldown and reload — popup will trigger fresh on the storefront
    localStorage.removeItem("rieave.popup.lastShownAt");
    toast.info("Cooldown reset. Open the storefront in a new tab to see the popup at the configured delay.");
  };

  if (!data) return <p className="text-cream/75">Loading…</p>;

  return (
    <div data-testid="admin-marketing-page">
      <p className="eyebrow text-cream/65 mb-1">Conversion</p>
      <h1 className="font-hero text-3xl text-cream mb-2">Marketing</h1>
      <p className="text-cream/65 text-sm mb-8 max-w-xl">
        Two conversion levers: a rotating announcement bar at the top of every page, and a delayed discount popup that nudges hesitant visitors toward checkout.
      </p>

      {/* MARQUEE */}
      <section className="bg-white rounded-2xl border border-border p-6 max-w-4xl mb-6" data-testid="marquee-form">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Megaphone size={16} className="text-rose" />
            <p className="eyebrow">Top announcement bar</p>
          </div>
          <button onClick={() => setMarquee("enabled", !data.marquee.enabled)} className="flex items-center gap-2 text-sm" data-testid="marquee-toggle">
            {data.marquee.enabled
              ? <><ToggleRight size={28} className="text-emerald-600" /> Enabled</>
              : <><ToggleLeft size={28} className="text-charcoal/50" /> Disabled</>}
          </button>
        </div>

        {/* Live preview */}
        <div className="rounded-lg overflow-hidden mb-5 border border-border" data-testid="marquee-preview">
          <p className="text-[10px] uppercase tracking-[0.22em] text-charcoal/50 px-3 py-1.5 bg-cream">Live preview</p>
          {(data.marquee.messages || []).length > 0 && data.marquee.enabled ? (
            <MarqueeBar
              messages={data.marquee.messages}
              speed={data.marquee.speed_seconds}
              bg={data.marquee.bg_color}
              fg={data.marquee.text_color}
              testId="marquee-preview-bar"
            />
          ) : (
            <p className="px-3 py-3 text-sm text-charcoal/50">{data.marquee.enabled ? "Add at least one message to see the preview." : "Bar is currently disabled."}</p>
          )}
        </div>

        {/* Messages list */}
        <p className="eyebrow mb-2">Rotating messages</p>
        <div className="space-y-2 mb-3">
          {(data.marquee.messages || []).map((m, i) => (
            <div key={i} className="flex gap-2" data-testid={`marquee-msg-${i}`}>
              <Input
                value={m}
                onChange={(e) => {
                  const arr = [...data.marquee.messages];
                  arr[i] = e.target.value;
                  setMarquee("messages", arr);
                }}
                placeholder="e.g. Limited stock — selling fast"
              />
              <button
                onClick={() => setMarquee("messages", data.marquee.messages.filter((_, idx) => idx !== i))}
                className="px-3 rounded-lg border border-border text-charcoal/60 hover:text-rose hover:border-rose"
                aria-label="remove"
                data-testid={`marquee-remove-${i}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <button
            onClick={() => setMarquee("messages", [...(data.marquee.messages || []), ""])}
            className="text-sm text-rose hover:text-rose/80 inline-flex items-center gap-1.5"
            data-testid="marquee-add-msg"
          >
            <Plus size={14} /> Add message
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-5">
          <div>
            <label className="eyebrow block mb-1">Sweep speed (seconds)</label>
            <Input type="number" min={8} max={120} value={data.marquee.speed_seconds} onChange={(e) => setMarquee("speed_seconds", Number(e.target.value))} data-testid="marquee-speed" />
            <p className="text-xs text-charcoal/55 mt-1">Lower = faster scroll. 25 is a calm default.</p>
          </div>
          <div>
            <label className="eyebrow block mb-1">Background color</label>
            <div className="flex gap-2">
              <input type="color" value={data.marquee.bg_color} onChange={(e) => setMarquee("bg_color", e.target.value)} className="w-12 h-10 rounded cursor-pointer" data-testid="marquee-bg" />
              <Input value={data.marquee.bg_color} onChange={(e) => setMarquee("bg_color", e.target.value)} />
            </div>
          </div>
          <div>
            <label className="eyebrow block mb-1">Text color</label>
            <div className="flex gap-2">
              <input type="color" value={data.marquee.text_color} onChange={(e) => setMarquee("text_color", e.target.value)} className="w-12 h-10 rounded cursor-pointer" data-testid="marquee-fg" />
              <Input value={data.marquee.text_color} onChange={(e) => setMarquee("text_color", e.target.value)} />
            </div>
          </div>
        </div>

        <button onClick={saveMarquee} disabled={savingMarquee} className="btn-primary mt-6 inline-flex items-center gap-2" data-testid="save-marquee">
          <Save size={14} /> {savingMarquee ? "Saving…" : "Save announcement bar"}
        </button>
      </section>

      {/* POPUP */}
      <section className="bg-white rounded-2xl border border-border p-6 max-w-4xl" data-testid="popup-form">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Gift size={16} className="text-rose" />
            <p className="eyebrow">Time-delayed discount popup</p>
          </div>
          <button onClick={() => setPopup("enabled", !data.popup.enabled)} className="flex items-center gap-2 text-sm" data-testid="popup-toggle">
            {data.popup.enabled
              ? <><ToggleRight size={28} className="text-emerald-600" /> Enabled</>
              : <><ToggleLeft size={28} className="text-charcoal/50" /> Disabled</>}
          </button>
        </div>

        <p className="text-sm text-charcoal/70 mb-5 max-w-2xl">
          Shows once per visitor (per cooldown window) after the configured delay. Automatically suppressed on cart, checkout, and account pages so it never interrupts a buying flow.
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="eyebrow block mb-1">Trigger delay (seconds)</label>
            <Input type="number" min={3} max={300} value={data.popup.delay_seconds} onChange={(e) => setPopup("delay_seconds", Number(e.target.value))} data-testid="popup-delay" />
            <p className="text-xs text-charcoal/55 mt-1">Industry sweet spot: 30–45 seconds.</p>
          </div>
          <div>
            <label className="eyebrow block mb-1">Re-show cooldown (hours)</label>
            <Input type="number" min={1} max={720} value={data.popup.cooldown_hours} onChange={(e) => setPopup("cooldown_hours", Number(e.target.value))} data-testid="popup-cooldown" />
            <p className="text-xs text-charcoal/55 mt-1">Don't pester the same visitor again for X hours.</p>
          </div>
          <div className="md:col-span-2">
            <label className="eyebrow block mb-1">Headline</label>
            <Input value={data.popup.headline} onChange={(e) => setPopup("headline", e.target.value)} placeholder="A little something before you go" data-testid="popup-headline" />
          </div>
          <div className="md:col-span-2">
            <label className="eyebrow block mb-1">Subheadline</label>
            <Textarea rows={2} value={data.popup.subheadline} onChange={(e) => setPopup("subheadline", e.target.value)} placeholder="Use this code at checkout for…" data-testid="popup-subheadline" />
          </div>
          <div>
            <label className="eyebrow block mb-1">Coupon code</label>
            <Input value={data.popup.coupon_code} onChange={(e) => setPopup("coupon_code", e.target.value.toUpperCase().replace(/\s+/g, ""))} placeholder="WELCOME10" data-testid="popup-code" />
            <p className="text-xs text-charcoal/55 mt-1">Make sure this exact code exists in <strong>/admin/coupons</strong>, otherwise it won't apply at checkout.</p>
          </div>
          <div>
            <label className="eyebrow block mb-1">CTA button text</label>
            <Input value={data.popup.button_text} onChange={(e) => setPopup("button_text", e.target.value)} placeholder="Copy code & shop" data-testid="popup-cta" />
          </div>
          <div className="md:col-span-2">
            <label className="eyebrow block mb-1">Image URL <span className="normal-case tracking-normal text-charcoal/55">(optional — pretty banner above the headline)</span></label>
            <Input value={data.popup.image_url} onChange={(e) => setPopup("image_url", e.target.value)} placeholder="https://…" data-testid="popup-image" />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-6">
          <button onClick={savePopup} disabled={savingPopup} className="btn-primary inline-flex items-center gap-2" data-testid="save-popup">
            <Save size={14} /> {savingPopup ? "Saving…" : "Save popup"}
          </button>
          <button onClick={previewPopup} className="btn-outline inline-flex items-center gap-2" data-testid="preview-popup">
            <Eye size={14} /> Reset cooldown (so I can preview)
          </button>
        </div>
      </section>
    </div>
  );
};

export default AdminMarketing;
